import { Theme } from "@mui/material";
import { Magnolia } from "domain/Magnolia";
import { ImageLink } from "domain/image";
import { getCurrentMultisiteBase, getCurrentLanguage, getLanguages } from "util/MagnoliaAppHelpers";

/**
 * Helper to inject language into path for single-tree structure (albis-distribution).
 * Converts paths like /albis-distribution/about -> /albis-distribution/en/about
 * Also handles production paths without multisite prefix: /about -> /en/about
 */
function injectLanguageIntoPath(href: string, multisiteBase: string, currentLang: string, isProduction: boolean = false): string {
  const multisitePrefix = `/${multisiteBase}`;
  const languages = getLanguages();
  
  // Handle production paths without multisite prefix (e.g., /about -> /en/about)
  if (isProduction && !href.startsWith(multisitePrefix)) {
    // Handle root path: / or empty -> /en
    if (href === '' || href === '/') {
      return `/${currentLang}`;
    }
    
    // Handle paths like /about/about-us -> /en/about/about-us
    if (href.startsWith('/')) {
      const firstSegment = href.substring(1).split('/')[0];
      
      // Only inject if language is not already present
      if (firstSegment && !languages.includes(firstSegment)) {
        return `/${currentLang}${href}`;
      }
    }
    
    return href;
  }
  
  // Handle: /albis-distribution -> /albis-distribution/en
  if (href === multisitePrefix) {
    return `${multisitePrefix}/${currentLang}`;
  }
  
  // Handle: /albis-distribution/about -> /albis-distribution/en/about
  if (href.startsWith(`${multisitePrefix}/`)) {
    const pathAfterMultisite = href.substring(multisitePrefix.length + 1);
    const firstSegment = pathAfterMultisite.split('/')[0];
    
    // Only inject if language is not already present
    if (!languages.includes(firstSegment)) {
      return href.replace(
        `${multisitePrefix}/`,
        `${multisitePrefix}/${currentLang}/`
      );
    }
  }
  
  return href;
}

export function normalizeHref(href: string) {
  // remove ;jsessionid= in case it is accidently maintained in magnolia
  // so it would not appear in html to create a caching in prerender
  // remove http:// and https:// because we don´t know which one is used
  const withoutSession = href.split(";jsessionid=")[0];
  // Whether the original link was an absolute URL (has a scheme + host). After
  // we strip the scheme below, an absolute URL becomes "host/path…" – the host
  // segment must be removed (prod) or replaced with the multisite base (dev),
  // otherwise it leaks into the path. See the host fallback below.
  const hadProtocol = /^https?:\/\//i.test(withoutSession);
  const trimmedHref = withoutSession
    .replaceAll("http://", "")
    .replaceAll("https://", "");

  const exceptions = [
    "www.mocom.eu",
    "stage.mocom.eu",
    "www.albis.com",
    "stage.albis.com",
    "www.ottokrahn.group",
    "stage.ottokrahn.group",
    "htf.krahn.eu",
    "stage.htf.krahn.eu",
    "www.krahn-ceramics.com",
    "stage.krahn-ceramics.com",
    "www.wipag.de",
    "stage.wipag.de",
    "www.wipag.com",
    "stage.wipag.com",
    "www.oknb.eu",
    "stage.oknb.eu",
    "www.krahn.eu",
    "stage.krahn.eu",
  ];

  const multisiteBase = getCurrentMultisiteBase();
  const isAlbisDistribution = multisiteBase === "albis-distribution";
  const currentLang = getCurrentLanguage();
  const isProduction = process.env.NODE_ENV === "production";

  // Store the processed href after handling domain exceptions
  let processedHref = trimmedHref;

  const currentException = exceptions.find((exception) =>
    trimmedHref.startsWith(exception)
  );
  if (currentException) {
    if (isProduction) {
      processedHref = trimmedHref.replace(currentException, "");
    } else {
      processedHref = trimmedHref.replace(currentException, `/${multisiteBase}`);
    }
  } else if (hadProtocol && !processedHref.startsWith("/")) {
    // Absolute URL whose host is not in the curated exceptions list – e.g. the
    // local/preview domains like `public.albis.local`. Without this the host
    // stays as the first path segment and, because the result has no leading
    // slash, react-router treats it as a RELATIVE redirect target and keeps
    // appending it on every render → an infinite "…/public.albis.local/markets/
    // public.albis.local/markets/…" loop. Strip the host segment, keeping the
    // path, and apply the same prod/dev treatment as the curated exceptions.
    const hostSegment = processedHref.split("/")[0];
    const path = processedHref.substring(hostSegment.length); // keeps leading "/"
    processedHref = isProduction ? path : `/${multisiteBase}${path}`;
  }

  //e.g. href: `https://stage.albis.com/albis-distribution/de`
  if (isProduction) {
    // in production the domain decides on which Page-Tree we work
    //e.g. return `https://stage.albis.com/de`
    
    // For albis-distribution (single-tree), inject language
    if (isAlbisDistribution) {
      // First inject language (handles both /albis-distribution/about and /about cases)
      const withLanguage = injectLanguageIntoPath(processedHref, multisiteBase, currentLang, true);
      
      // Now remove the multisite base for production if present
      // Handle both /albis-distribution/ and /albis-distribution patterns
      if (withLanguage.startsWith(`/${multisiteBase}/`)) {
        return withLanguage.substring(multisiteBase.length + 1); // +1 for the leading slash
      } else if (withLanguage === `/${multisiteBase}`) {
        return "/";
      }
      
      return withLanguage;
    }
    
    return processedHref.replace(`/${multisiteBase}/`, "/");
  } else {
    // in development we have only one domain (localhost) and therefore need this distinction
    //e.g. return `http://localhost:41812/albis-distribution/de`
    
    // For albis-distribution (single-tree), inject language into the path
    if (isAlbisDistribution) {
      return injectLanguageIntoPath(processedHref, multisiteBase, currentLang, false);
    }
    
    return processedHref;
  }
}

export function getHref(link: Magnolia.Link) {
  switch (link.field) {
    case "page":
      return link.pageLink ? normalizeHref(link.pageLink) : "";
    case "external":
      return link.externalLink ?? "";
    case "download":
      return link.downloadLink ? normalizeHref(link.downloadLink) : "";
    default:
      return "";
  }
}

export function isExternalLink(link: Magnolia.Link) {
  switch (link.field) {
    case "page":
      return false;
    case "external":
      return true;
    case "download":
      return true;
    default:
      return false;
  }
}

// optional link around an image; undefined if the editor did not maintain a target
export function getImageLink(link?: Magnolia.Link): ImageLink | undefined {
  const href = link ? getHref(link) : "";

  return link && href ? { href, external: isExternalLink(link) } : undefined;
}

export function getColorFromTheme(color: Magnolia.Color, theme: Theme) {
  switch (color) {
    case "common.white":
      return theme.palette.common.white;
    case "primary.dark":
      return theme.palette.primary.dark;
    case "primary.light":
      return theme.palette.primary.light;
    case "primary.main":
      return theme.palette.primary.main;
    case "text.primary":
      return theme.palette.text.primary;
    case "text.secondary":
      return theme.palette.text.secondary;
    case "secondary.main":
      return theme.palette.secondary.main;
    case "secondary.light":
      return theme.palette.secondary.light;
    case "info.main":
      return theme.palette.info.main;
    case "warning.main":
      return theme.palette.warning.main;
    case "success.main":
      return theme.palette.success.main;
    case "lightgray":
      return "#E8EBED";
    case "jobpages.primary":
      return theme.palette.jobpages?.primary ?? theme.palette.primary.main;
    case "jobpages.secondary":
      return theme.palette.jobpages?.secondary ?? theme.palette.primary.main;
    case "jobpages.tertiary":
      return theme.palette.jobpages?.tertiary ?? theme.palette.primary.main;
    default:
      return theme.palette.primary.main;
  }
}

/**
 * Magnolia produces crazy structures e.g. when using a multifield.
 * Instead of using an array, the object contains multiple enumerated keys as
 *
 * { multifieldProp0: valueA, multifieldProp1: valueB}
 *
 * This function creates an array, when given such object,
 * the magnolia multifield name, and the @nodes property
 * contained as metadata from Magnolia
 *
 * => [valueA, valueB]
 *
 * @param multifieldKey
 * @param jcrData
 * @param nodes
 */
export function convertMultifieldStructure<ResultType = Magnolia.ContentNode>(
  // e.g. multiFieldKey = "abc"
  multifieldKey: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  jcrData: any,
  nodes: string[]
) {
  const values: ResultType[] = [];
  nodes
    .filter((nodeKey) => {
      // e.g. `nodeKey = "abc0"`
      return (
        // make sure the wanted key is included
        nodeKey.includes(multifieldKey) &&
        // and make sure it's not only included but the whole key (the rest should be a number only)
        // this prevents the logic from matching `nodeKey = "abcdef0"`
        !isNaN(Number(nodeKey.replace(multifieldKey, "")))
      );
    })
    .forEach((nodeKey) => {
      if (jcrData[nodeKey]) {
        values.push(jcrData[nodeKey] as ResultType);
      } else {
        // this should not happen
      }
    });

  return values;
}

export function formatFileSize(number: number) {
  const bytesToKB = number / 1024;
  return new Intl.NumberFormat("de-DE", {
    maximumFractionDigits: 0,
  }).format(bytesToKB);
}
